import { PapiTag } from '@wix/da-papi-types';
import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { BiData, DreamupInfoClickBiEvent } from '@wix/da-bi/pkg/events';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import DreamUpStarsIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import EmptyResult from '../../../EmptyResult';
import OrderDropdown from '../../_partials/OrderDropdown';
import BasePageLayout from '../../_partials/BasePageLayout';
import TagSliderSubnav from '../../_partials/TagSliderSubnav';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import { DailyChallengeArray } from './DailyChallenges/types';
import DailyChallengePageHeader from './DailyChallenges/DailyChallengePageHeader';
import SelectedDailyChallengeHeading from './DailyChallenges/SelectedDailyChallengeHeading';

import s from './TagPage.scss';

export interface Props {
  tag?: PapiTag;
  hasOrderSubnav?: boolean;
  dailyChallenges?: DailyChallengeArray;
}

const TagPage: React.FC<Props> = ({ tag, hasOrderSubnav, dailyChallenges }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isDreamUpTpCta = useFlag('dreamup_tp_cta');
  const topic_tag_unification = useFlag('topic_tag_unification');
  const { aiContentFilter } = useContext(SessionContext);

  // precondition: backend won't send dailyChallenges unless we've matched a tag in them
  const showDailyChallenges = !!dailyChallenges;

  if (!tag) {
    return <EmptyResult />;
  }

  if (showDailyChallenges) {
    return (
      <BasePageLayout
        customHeader={
          <DailyChallengePageHeader
            tag={tag.tag}
            dailyChallenges={dailyChallenges}
          />
        }
        withTabHeader={false}
        withNetworkBar={false}
        withNavigation={false}
        contentClassName={s['daily-challeges-content-block']}
      >
        <SelectedDailyChallengeHeading
          className={s['current-challenge-header']}
          tag={tag.tag}
          tagItemId={tag.itemId}
          dailyChallenges={dailyChallenges}
        />

        <DefaultPageContent />
      </BasePageLayout>
    );
  }

  const showDreamUpCTA = aiContentFilter === 'allow';
  const dreamupCTAButtonMedium = (
    <GhostButton
      variant="main"
      size="medium"
      icon={isDreamUpTpCta ? DreamUpStarsIcon : DreamUpIcon}
      href={Url.dreamupLink({ prompt: tag.tag })}
      biData={BiData<DreamupInfoClickBiEvent>({
        evid: 923,
        typeid: ResourceType.DISCOVERYTAG,
        itemid: tag.itemId,
      })}
      className={s['dreamup-cta-button']}
    >
      {t('tagPage.headline.dreamupCta.label')}
    </GhostButton>
  );
  const dreamupCTAButtonLarge = (
    <GhostButton
      variant="main"
      size="large"
      icon={isDreamUpTpCta ? DreamUpStarsIcon : DreamUpIcon}
      href={Url.dreamupLink({ prompt: tag.tag })}
      biData={BiData<DreamupInfoClickBiEvent>({
        evid: 923,
        typeid: ResourceType.DISCOVERYTAG,
        itemid: tag.itemId,
      })}
      className={s['dreamup-cta-button']}
    >
      {t('tagPage.headline.dreamupCta.label')}
    </GhostButton>
  );

  const headerLeftSide = isMobile ? (
    <h1>{topic_tag_unification ? tag.name : `#${tag.tag}`}</h1>
  ) : (
    <div className={s['headline-with-cta']}>
      <h1 className={s['title']}>
        {t('tagPage.headline', {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          tag: (
            <span className={s['title-tag']}>
              {topic_tag_unification ? tag.name : tag.tag}
            </span>
          ),
        })}
      </h1>
      {showDreamUpCTA && isDreamUpTpCta && dreamupCTAButtonMedium}
      {showDreamUpCTA && !isDreamUpTpCta && dreamupCTAButtonLarge}
    </div>
  );

  return (
    <BasePageLayout
      wrapperClassName={s['root']}
      headerIsStickyOnScrollUp={topic_tag_unification ? false : undefined}
      headerLeftSideContent={headerLeftSide}
      headerRightSideContent={
        hasOrderSubnav && <OrderDropdown className={s['order-dropdown']} />
      }
      withNetworkBar={false}
      withNavigation={false}
      contentClassName={classnames(
        s['content-block'],
        topic_tag_unification && s['topic_tag_unification']
      )}
    >
      {topic_tag_unification && (
        <TagSliderSubnav className={s['tag-slider-subnav']} />
      )}

      {isMobile && showDreamUpCTA && (
        <div
          className={classnames(
            s['mobile-cta-wrapper'],
            topic_tag_unification && s['topic_tag_unification']
          )}
        >
          {dreamupCTAButtonLarge}
        </div>
      )}
      <DefaultPageContent />
    </BasePageLayout>
  );
};
TagPage.displayName = 'TagPage';

export default TagPage;
